/**
 * Client Detail Page
 *
 * This page provides a comprehensive view of a single client including:
 * - Client information and settings
 * - Admin users management
 * - Usage statistics and analytics
 * - Quick actions (suspend/activate, edit)
 */

import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation } from "@apollo/client";
import { useState } from "react";
import {
  ArrowLeftIcon,
  PencilIcon,
  UserGroupIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  PlusIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { ActivQMSLoader } from "../../components/ui/activqms-loader";
import {
  GET_CLIENT,
  GET_CLIENT_STATISTICS,
  SUSPEND_CLIENT,
  ACTIVATE_CLIENT,
  LIST_CLIENTS,
  ADD_CLIENT_ADMIN,
  REMOVE_CLIENT_ADMIN,
} from "../../services/client-api";

export function ClientDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const {
    data: clientData,
    loading: clientLoading,
    error: clientError,
  } = useQuery(GET_CLIENT, {
    variables: { clientId: id },
    skip: !id,
  });

  const { data: statsData } = useQuery(GET_CLIENT_STATISTICS, {
    variables: { clientId: id },
    skip: !id,
  });

  const [suspendClient] = useMutation(SUSPEND_CLIENT, {
    refetchQueries: [
      { query: GET_CLIENT, variables: { clientId: id } },
      { query: LIST_CLIENTS },
    ],
  });

  const [activateClient] = useMutation(ACTIVATE_CLIENT, {
    refetchQueries: [
      { query: GET_CLIENT, variables: { clientId: id } },
      { query: LIST_CLIENTS },
    ],
  });

  const client = clientData?.getClient;
  const statistics = statsData?.getClientStatistics || client?.statistics;

  const handleStatusToggle = async () => {
    if (!client) return;

    try {
      if (client.status === "ACTIVE") {
        await suspendClient({ variables: { clientId: id } });
      } else {
        await activateClient({ variables: { clientId: id } });
      }
    } catch (error) {
      console.error("Error updating client status:", error);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusStyles = {
      ACTIVE: "bg-green-100 text-green-800",
      SUSPENDED: "bg-red-100 text-red-800",
      PENDING: "bg-yellow-100 text-yellow-800",
      CANCELLED: "bg-gray-100 text-gray-800",
    };

    return (
      <span
        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
          statusStyles[status as keyof typeof statusStyles] ||
          statusStyles.PENDING
        }`}
      >
        {status}
      </span>
    );
  };

  const getTierBadge = (tier: string) => {
    const tierStyles = {
      FREE: "bg-gray-100 text-gray-800",
      BASIC: "bg-blue-100 text-blue-800",
      PROFESSIONAL: "bg-purple-100 text-purple-800",
      ENTERPRISE: "bg-gold-100 text-gold-800",
    };

    return (
      <span
        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
          tierStyles[tier as keyof typeof tierStyles] || tierStyles.FREE
        }`}
      >
        {tier}
      </span>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (clientLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <ActivQMSLoader size="lg" text="Loading client details..." />
      </div>
    );
  }

  if (clientError || !client) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate("/clients")}
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-1" />
            Back to Clients
          </button>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="text-sm text-red-700">
            {clientError
              ? `Error loading client: ${clientError.message}`
              : "Client not found"}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate("/clients")}
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-1" />
            Back to Clients
          </button>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={handleStatusToggle}
            className={`inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md ${
              client.status === "ACTIVE"
                ? "text-red-700 bg-red-100 hover:bg-red-200"
                : "text-green-700 bg-green-100 hover:bg-green-200"
            }`}
          >
            {client.status === "ACTIVE" ? "Suspend" : "Activate"}
          </button>
          <Link
            to={`/clients/${client.id}/edit`}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            <PencilIcon className="h-4 w-4 mr-1" />
            Edit
          </Link>
        </div>
      </div>

      {/* Client Overview */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {client.clientName}
              </h1>
              <p className="mt-1 text-sm text-gray-600">
                Client ID: {client.clientId}
              </p>
            </div>
            <div className="flex items-center space-x-3">
              {getStatusBadge(client.status)}
              {getTierBadge(client.subscriptionTier)}
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            <div>
              <dt className="text-sm font-medium text-gray-500">
                Contact Email
              </dt>
              <dd className="mt-1 text-sm text-gray-900">
                {client.contactEmail}
              </dd>
            </div>
            {client.contactPhone && (
              <div>
                <dt className="text-sm font-medium text-gray-500">
                  Contact Phone
                </dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {client.contactPhone}
                </dd>
              </div>
            )}
            <div>
              <dt className="text-sm font-medium text-gray-500">Created</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {formatDate(client.createdAt)}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">
                Last Updated
              </dt>
              <dd className="mt-1 text-sm text-gray-900">
                {formatDate(client.updatedAt)}
              </dd>
            </div>
            {statistics?.lastActivityAt && (
              <div>
                <dt className="text-sm font-medium text-gray-500">
                  Last Activity
                </dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {formatDate(statistics.lastActivityAt)}
                </dd>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <UserGroupIcon className="h-6 w-6 text-blue-500" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Total Users
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {statistics?.totalUsers || 0}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <UserGroupIcon className="h-6 w-6 text-green-500" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Active Users
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {statistics?.activeUsers || 0}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ChartBarIcon className="h-6 w-6 text-purple-500" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Storage Used
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {statistics?.storageUsedGB?.toFixed(1) || "0.0"} GB
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Cog6ToothIcon className="h-6 w-6 text-yellow-500" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Files Uploaded
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {statistics?.filesUploaded || 0}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Admin Users */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
            Admin Users
          </h3>

          {client.adminUsers && client.adminUsers.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Last Login
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {client.adminUsers.map((user: any) => (
                    <tr key={user.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {user.firstName} {user.lastName}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {user.email}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(user.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {user.lastLoginAt
                          ? formatDate(user.lastLoginAt)
                          : "Never"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(user.createdAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-6">
              <div className="text-sm text-gray-500">No admin users found.</div>
            </div>
          )}
        </div>
      </div>

      {/* Settings */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
            Client Settings
          </h3>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
              <dt className="text-sm font-medium text-gray-500">Max Users</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {client.settings?.maxUsers === -1
                  ? "Unlimited"
                  : client.settings?.maxUsers || 0}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Max Storage</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {client.settings?.maxStorageGB === -1
                  ? "Unlimited"
                  : `${client.settings?.maxStorageGB || 0} GB`}
              </dd>
            </div>
            <div className="sm:col-span-2">
              <dt className="text-sm font-medium text-gray-500">
                Features Enabled
              </dt>
              <dd className="mt-1">
                <div className="flex flex-wrap gap-2">
                  {client.settings?.featuresEnabled?.map((feature: string) => (
                    <span
                      key={feature}
                      className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800"
                    >
                      {feature
                        .replace(/_/g, " ")
                        .toLowerCase()
                        .replace(/\b\w/g, (l) => l.toUpperCase())}
                    </span>
                  )) || (
                    <span className="text-sm text-gray-500">
                      No features enabled
                    </span>
                  )}
                </div>
              </dd>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
